import type { EnrichedMessage, Message, RequestOutcome } from "./types";

const failed = (message: EnrichedMessage) =>
  message.result?.checkStatus === "failed";

export const requestOutcome = (
  request: Message,
  replies: EnrichedMessage[],
): RequestOutcome | undefined => {
  if (!["completed", "failed", "cancelled"].includes(request.status ?? ""))
    return undefined;
  const results = replies.filter((message) => message.result);
  const last = results.at(-1);
  const newestFirst = [...results].reverse();
  const saved = newestFirst.find(
    (message) => message.result?.applied === true && !failed(message),
  );
  const alreadySet = newestFirst.find(
    (message) =>
      message.result?.kind === "saved_changes" &&
      message.result.already_set === true &&
      !failed(message),
  );
  if (
    request.status === "completed" &&
    last?.result?.kind === "clarification" &&
    !saved
  )
    return undefined;
  const validation = newestFirst.find(
    (message) => message.result?.kind === "validation",
  );
  const relevant = newestFirst.find(
    (message) =>
      message.result?.kind === "calculation_comparison" ||
      (message.id === validation?.id && failed(message)) ||
      (message.result?.kind === "saved_changes" &&
        failed(message) &&
        (!saved || message.sequence > saved.sequence)),
  );
  const result =
    relevant &&
    (!saved || failed(relevant) || relevant.sequence > saved.sequence)
      ? relevant.result
      : (saved ?? alreadySet)?.result;
  const base = {
    saved: !!saved,
    result,
    retry: replies.some((message) => message.retryable),
  };
  if (request.status === "cancelled")
    return {
      ...base,
      title: "Request cancelled",
      note: [
        request.error?.trim() ||
          "Any saved changes were kept. Review your proposal before you continue.",
        request.proposal_retained && "Your proposed change is retained.",
      ]
        .filter(Boolean)
        .join(" "),
      retry: false,
    };
  if (request.status === "failed")
    return {
      ...base,
      title: "Request stopped",
      note: [
        request.error?.trim() ||
          "This request may have saved changes. Review your proposal, then send a new request for any work that remains.",
        request.proposal_retained && "Your proposed change is retained.",
      ]
        .filter(Boolean)
        .join(" "),
      retry: false,
      support: true,
    };
  if (relevant?.result?.checkStatus === "failed")
    return {
      ...base,
      title: relevant.result.checkTitle,
      note: request.proposal_retained
        ? "Your proposed change is retained. Correct the check or send the request again to continue."
        : saved
          ? "An earlier change was saved. The remaining change could not be confirmed."
          : "This change was not saved. Correct the remaining problems before you continue.",
    };
  if (saved) return { ...base, title: "Saved to your proposal" };
  if (relevant?.result?.checkStatus === "passed")
    return {
      ...base,
      title: relevant.result.checkTitle,
      note: "No change was saved by this request.",
    };
  if (alreadySet && !relevant)
    return {
      ...base,
      title: "Already matches your request",
      note: "No change was needed or saved by this request.",
    };
  if (
    !results.length &&
    (replies.some((message) => message.content.trim() && !message.retryable) ||
      (replies.length > 0 &&
        replies.every((message) => !message.content.trim())))
  )
    return undefined;
  return {
    ...base,
    title: "Checks incomplete",
    note: "This request did not confirm a saved change. Ask the agent to finish the checks.",
  };
};
